import { mapKeys, camelCase } from 'lodash';
import { getUser, updateUser, getUserDetails } from '../api/users.ts';
import RailsUtility from '../utilities/rails.ts';

const generalStore = {
  namespaced: true,
  state: {
    alert: false,
    alertType: '',
    alertTitle: '',
    alertDescription: '',
    alertUrl: '',
    loadingSpinner: false,
    localLoadingSpinner: false,
    iconsOnly: false,
    messagesToday: 0,
    contactListContext: {
      currentIndex: null,
      records: [],
      label: null,
    },
    setupBanner: false,
    teamSetupProgress: 100,
    breadcrumbs: [],
    currentUser: {
      timezone: '',
      firstName: '',
      lastName: '',
      onboardingFlow: '',
      eventBuffer: '',
      teamSlug: '',
      postmarkEmailVerified: false,
      emailSignature: '',
      unconfirmedEmail: '',
      email: '',
    },
    currentUserCalendar: {
      isGoogleAuthenticated: false,
      isMicrosoftAuthenticated: false,
    },
    icaAgreementSessionId: null,
    currentUserAvailability: {
      sunday: {
        isAvailable: false,
        startTime: '',
        endTime: '',
      },
      monday: {
        isAvailable: false,
        startTime: '',
        endTime: '',
      },
      tuesday: {
        isAvailable: false,
        startTime: '',
        endTime: '',
      },
      wednesday: {
        isAvailable: false,
        startTime: '',
        endTime: '',
      },
      thursday: {
        isAvailable: false,
        startTime: '',
        endTime: '',
      },
      friday: {
        isAvailable: false,
        startTime: '',
        endTime: '',
      },
      saturday: {
        isAvailable: false,
        startTime: '',
        endTime: '',
      },
    },
    currentUserNotificationSettings: {
      notifyOnNewMessage: false,
      notifyOnNewApplication: false,
      notifyDailyPipelineReport: false,
      notifyWeeklyDigestReport: false,
      notifyOnEventScheduled: false,
      notifyOnEventReminderEmail: false,
      notifyOnEventReminderSMS: false,
      notifyOnStaleJobs: false,
    },
    currentUserAccountDetails: {
      username: '',
      unreadMessages: 0,
      incompleteTasks: 0,
      locked: false,
      role: '',
      logo: '',
      isImpersonated: false,
      scheduleFollowupLink: '',
      featureFlags: {},
      onboardingPath: '',
      isOnboardingCompleted: false,
    },
  },
  actions: {
    async loadCurrentUser({ commit, dispatch }) {
      commit('showLocalLoadingSpinner');
      const data = await getUser();
      commit('setIcaAgreementSessionId', {
        icaAgreementSessionId: data.ica_agreement_session_id,
      });
      commit('setCurrentUser', {
        currentUser: data.record,
        // extract timezone, teamSlug, and eventBuffer now, not nested within data.record
        timezone: data?.user_calendar?.timezone ?? 'Pacific Time (US & Canada)',
        teamSlug: data.team_slug,
        eventBuffer: data?.user_calendar?.event_buffer ?? 0,
      });
      commit('setCurrentUserAvailability', {
        availability: data.user_calendar_availability,
      });
      commit('setCurrentUserNotificationSettings', {
        notificationSettings: data.record,
      });
      await dispatch('loadCurrentUserAccountDetails');
      commit('hideLocalLoadingSpinner');
    },
    async updateUser({ dispatch, state }, { user, updatedUserAttributes }) {
      await updateUser(user, { updatedUserAttributes });
      // if user is not set in state, do so
      if (user.id === state.user?.id) await dispatch('loadCurrentUser');
    },
    async loadCurrentUserAccountDetails({ commit }) {
      const data = await getUserDetails();
      commit('setCurrentUserAccountDetails', {
        details: {
          username: data.username,
          unreadMessages: data.unread_count,
          incompleteTasks: data.incomplete_tasks,
          locked: data.locked,
          role: data.role,
          logo: data.logo,
          isImpersonated: data.impersonated,
          scheduleFollowupLink: data.schedule_followup_link,
          featureFlags: data.feature_flags,
          onboardingPath: data.onboarding_path,
          isOnboardingCompleted: data.onboarding_completed,
          teamSlug: data.team_slug,
          showQuickstart: data.show_quickstart,
        },
      });
    },
  },
  mutations: {
    setContactListContext(state, { records, currentIndex, label }) {
      state.contactListContext.records = records || state.contactListContext.records;
      state.contactListContext.label = label || state.contactListContext.label;
      state.contactListContext.currentIndex = currentIndex;
    },
    clearContactListContext(state) {
      state.contactListContext.records = [];
      state.contactListContext.currentIndex = null;
    },
    showAlert(state, { alertType, alertTitle, alertDescription, alertUrl }) {
      state.alert = true;
      state.alertType = alertType;
      state.alertTitle = alertTitle;
      state.alertDescription = alertDescription;
      state.alertUrl = alertUrl;
      setTimeout(function () {
        state.alert = false;
        state.alertType = '';
      }, 3000);
    },
    dismissAlert(state) {
      state.alert = false;
      state.alertType = '';
    },
    showLoadingSpinner(state) {
      state.loadingSpinner = true;
    },
    hideLoadingSpinner(state) {
      state.loadingSpinner = false;
    },
    showLocalLoadingSpinner(state) {
      state.localLoadingSpinner = true;
    },
    hideLocalLoadingSpinner(state) {
      state.localLoadingSpinner = false;
    },
    setUnreadMessages(state, { messageCount }) {
      state.unreadMessages = messageCount;
    },
    setAccountDetails(state, { details }) {
      state.username = details.username;
      state.unreadMessages = details.unreadMessages;
      state.messagesToday = details.messagesToday;
      state.role = details.role;
      state.logo = details.logo;
      state.isImpersonated = details.isImpersonated;
      state.scheduleFollowupLink = details.scheduleFollowupLink;
      state.featureFlags = details.featureFlags;
      state.onboardingPath = details.onboardingPath;
      state.showQuickstart = details.showQuickstart;
      state.incompleteTasks = details.incompleteTasks;
    },
    showMenuNames(state) {
      state.iconsOnly = false;
    },
    hideMenuNames(state) {
      state.iconsOnly = true;
    },
    setTeamSetupProgress(state, { teamSetupProgress }) {
      if (state.teamSetupProgress != teamSetupProgress) {
        state.teamSetupProgress = teamSetupProgress;
        state.setupBanner = state.teamSetupProgress < 67 ? true : false;
      }
    },
    hideSetupBanner(state) {
      state.setupBanner = false;
    },
    setBreadcrumbs(state, breadcrumbs) {
      state.breadcrumbs = breadcrumbs;
    },
    setIcaAgreementSessionId(state, { icaAgreementSessionId }) {
      state.icaAgreementSessionId = icaAgreementSessionId;
    },
    setCurrentUser(state, { currentUser, timezone, teamSlug, eventBuffer }) {
      state.currentUser = mapKeys(currentUser, (v, k) => camelCase(k));
      state.currentUser.timezone = timezone;
      state.currentUser.teamSlug = teamSlug;
      state.currentUser.eventBuffer = RailsUtility.convertFromEnumFormat(eventBuffer);
    },
    setCurrentUserCalendar(state, { isGoogleAuthenticated, isMicrosoftAuthenticated }) {
      state.currentUserCalendar.isGoogleAuthenticated = isGoogleAuthenticated;
      state.currentUserCalendar.isMicrosoftAuthenticated = isMicrosoftAuthenticated;
    },
    setCurrentUserAvailability(state, { availability }) {
      for (let slot of availability) {
        state.currentUserAvailability[slot.day_of_week].startTime = slot.start_time;
        state.currentUserAvailability[slot.day_of_week].endTime = slot.end_time;
        state.currentUserAvailability[slot.day_of_week].isAvailable = slot.is_available;
      }
    },
    setCurrentUserNotificationSettings(state, { notificationSettings }) {
      state.currentUserNotificationSettings.notifyOnNewMessage = notificationSettings.notify_on_new_message;
      state.currentUserNotificationSettings.notifyOnNewApplication = notificationSettings.notify_on_new_application;
      state.currentUserNotificationSettings.notifyDailyPipelineReport =
        notificationSettings.notify_daily_pipeline_report;
      state.currentUserNotificationSettings.notifyWeeklyDigestReport = notificationSettings.notify_weekly_digest_report;
      state.currentUserNotificationSettings.notifyOnEventScheduled = notificationSettings.notify_on_event_scheduled;
      state.currentUserNotificationSettings.notifyOnEventReminderEmail =
        notificationSettings.notify_on_event_reminder_email;
      state.currentUserNotificationSettings.notifyOnEventReminderSMS =
        notificationSettings.notify_on_event_reminder_sms;
      state.currentUserNotificationSettings.notifyOnStaleJobs = notificationSettings.notify_on_stale_jobs;
    },
    setCurrentUserAccountDetails(state, { details }) {
      state.currentUserAccountDetails = details;
    },
  },
};

export default generalStore;
